import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_URL } from '../config';
import { initializePokemonMaps } from '../utils/pokemonMap';

// Async thunk for fetching all Pokemon
export const fetchAllPokemon = createAsyncThunk(
  'pokemon/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_URL}/pokemon`);
      if (!response.ok) {
        throw new Error('Failed to fetch Pokemon data');
      }
      const data = await response.json();
      if (Array.isArray(data.pokemon)) {
        initializePokemonMaps(data.pokemon);
        return data.pokemon;
      }
      throw new Error('Invalid Pokemon data format');
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching Pokemon details
export const fetchPokemonById = createAsyncThunk(
  'pokemon/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_URL}/pokemon/${id}`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Pokemon not found');
        }
        throw new Error('Failed to fetch Pokemon details');
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const pokemonSlice = createSlice({
  name: 'pokemon',
  initialState: {
    allPokemon: [],
    pokemonDetails: {},
    loading: {
      all: false,
      byId: {}
    },
    error: {
      all: null,
      byId: {}
    }
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Handle fetchAllPokemon
      .addCase(fetchAllPokemon.pending, (state) => {
        state.loading.all = true;
        state.error.all = null;
      })
      .addCase(fetchAllPokemon.fulfilled, (state, action) => {
        state.allPokemon = action.payload;
        state.loading.all = false;
        state.error.all = null;
      })
      .addCase(fetchAllPokemon.rejected, (state, action) => {
        state.loading.all = false;
        state.error.all = action.payload;
      })
      // Handle fetchPokemonById
      .addCase(fetchPokemonById.pending, (state, action) => {
        const id = action.meta.arg;
        state.loading.byId[id] = true;
        state.error.byId[id] = null;
      })
      .addCase(fetchPokemonById.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.pokemonDetails[id] = action.payload;
        state.loading.byId[id] = false;
        state.error.byId[id] = null;
      })
      .addCase(fetchPokemonById.rejected, (state, action) => {
        const id = action.meta.arg;
        state.loading.byId[id] = false;
        state.error.byId[id] = action.payload;
      });
  },
});

// Selectors
export const selectAllPokemon = (state) => state.pokemon.allPokemon;
export const selectPokemonById = (id) => (state) => state.pokemon.pokemonDetails[id];
export const selectPokemonLoading = (state) => state.pokemon.loading;
export const selectPokemonError = (state) => state.pokemon.error;

export default pokemonSlice.reducer; 